import React, {useMemo, useState} from "react";
import {
    AlertTriangle,
    Brush,
    Clock,
    FileText,
    Hammer,
    Hourglass,
    Layers,
    Pencil,
    Plus,
    Trash2,
    Users,
    Wallet,
} from "lucide-react";
import {addMonths} from "date-fns";
import {toKSTISOString} from "../../components/commonmethod/CommonMethod";
import {ManagedAssetDoughnut, ManagedAssetLineArea} from "../chart/ManagedAssetStatic";

const HISTORY_TITLE = "자산관리내역";

const WEEKDAYS = [
    {label: "일", className: "text-[#ef4444]"},
    {label: "월", className: "text-[#374151]"},
    {label: "화", className: "text-[#374151]"},
    {label: "수", className: "text-[#374151]"},
    {label: "목", className: "text-[#374151]"},
    {label: "금", className: "text-[#374151]"},
    {label: "토", className: "text-[#3b82f6]"},
];

const HISTORY_TAG_STYLE = {
    "신규": {color: "#1d4ed8", bg: "#dbeafe"},
    "변경": {color: "#047857", bg: "#d1fae5"},
    "삭제": {color: "#b91c1c", bg: "#fee2e2"},
};
const DEFAULT_TAG_STYLE = {color: "#374151", bg: "#f3f4f6"};

const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

function buildMonthCells(viewMonth) {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekday; i++) {
        const day = daysInPrevMonth - firstWeekday + i + 1;
        cells.push({date: new Date(year, month - 1, day), inMonth: false});
    }
    for (let day = 1; day <= daysInMonth; day++) {
        cells.push({date: new Date(year, month, day), inMonth: true});
    }
    let nextDay = 1;
    while (cells.length % 7 !== 0) {
        cells.push({date: new Date(year, month + 1, nextDay), inMonth: false});
        nextDay += 1;
    }
    return cells;
}

const ITEM_ICON = {
    "전체": Wallet,
    "신규": Plus,
    "변경": Pencil,
    "삭제": Trash2,
    "감가 1개월전": Clock,
    "감가 3개월전": Clock,
    "EOS 만료 3개월전": Hourglass,
    "EOS 만료": AlertTriangle,
    "통합유지보수": Brush,
    "개별유지보수": Layers,
    "ACS(인력지원)": Users,
    "인프라(콜센터)": Hammer,
};

const ITEM_ICON_BG = {
    "전체": "#94a3b8",
    "신규": "#5bc0de",
    "변경": "#5cb85c",
    "삭제": "#d74b4b",
    "감가 1개월전": "#94a3b8",
    "감가 3개월전": "#5bc0de",
    "EOS 만료 3개월전": "#5cb85c",
    "EOS 만료": "#d74b4b",
    "통합유지보수": "#94a3b8",
    "개별유지보수": "#5bc0de",
    "ACS(인력지원)": "#5cb85c",
    "인프라(콜센터)": "#d74b4b",
};

const DEFAULT_ICON_BG = "#94a3b8";

export const sumCnt = (items) =>
    (items ?? []).reduce((acc, row) => acc + (Number(row?.cnt) || 0), 0);

export const formatPeriod = (search) => {
    if (!search?.startDate || !search?.endDate) return "";
    return `${search.startDate.substring(2, 10)} ~ ${search.endDate.substring(2, 10)}`;
};

export const extractResponseData = (settled) => {
    if (settled?.status !== "fulfilled") return null;
    const response = settled.value;
    if (response?.status !== 200) return null;
    return response?.data ?? null;
};

function SummaryRow({label, value, icon: Icon, iconBg}) {
    return (
        <div className="flex h-full w-full items-center gap-[10px] rounded-[8px] border border-[#e5e5e5] bg-white px-[10px] py-[6px] text-left text-[14px] text-[#333]">
            {Icon && (
                <span
                    className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full"
                    style={{background: iconBg ?? DEFAULT_ICON_BG}}
                >
                    <Icon size={14} strokeWidth={2.2} className="text-white" />
                </span>
            )}
            <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
            <span className="shrink-0 text-[13px] text-[#666]">
                {(Number(value) || 0).toLocaleString()}건
            </span>
        </div>
    );
}

export function HistoryCalendarCard({history, onReload}) {
    const today = useMemo(() => new Date(), []);
    const [selectedDate, setSelectedDate] = useState(today);
    const [viewMonth, setViewMonth] = useState(() => startOfMonth(today));

    const dotSet = useMemo(() => new Set(history?.dots ?? []), [history?.dots]);
    const delayDotSet = useMemo(() => new Set(history?.delayDots ?? []), [history?.delayDots]);
    const monthCells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
    const dayItems = history?.data ?? [];

    const handleSelectDate = (date) => {
        setSelectedDate(date);
        onReload?.({
            startDate: toKSTISOString(addMonths(date, -1)),
            endDate: toKSTISOString(addMonths(date, 1)),
            clickedDate: toKSTISOString(date),
        });
    };

    const changeMonth = (delta) => {
        const newMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1);
        setViewMonth(newMonth);
        onReload?.({
            startDate: toKSTISOString(addMonths(newMonth, -1)),
            endDate: toKSTISOString(addMonths(newMonth, 1)),
            clickedDate: toKSTISOString(selectedDate),
        });
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col border rounded-si p-[16px]">
            <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">{HISTORY_TITLE}</h3>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
                <div className="flex min-h-[280px] min-w-0 flex-1 flex-col md:min-h-0">
                    <div className="flex shrink-0 items-center justify-between border-b border-[#e5e7eb]">
                        <button
                            type="button"
                            aria-label="이전 달"
                            onClick={() => changeMonth(-1)}
                            className="flex h-7 w-7 items-center justify-center rounded text-[16px] leading-none text-[#9ca3af] hover:text-[#374151]"
                        >
                            ‹
                        </button>
                        <span className="text-[14px] font-semibold text-[#6b7c93]">
                            {viewMonth.getFullYear()}.{String(viewMonth.getMonth() + 1).padStart(2, "0")}
                        </span>
                        <button
                            type="button"
                            aria-label="다음 달"
                            onClick={() => changeMonth(1)}
                            className="flex h-7 w-7 items-center justify-center rounded text-[16px] leading-none text-[#9ca3af] hover:text-[#374151]"
                        >
                            ›
                        </button>
                    </div>

                    <div className="mt-1 grid shrink-0 grid-cols-7 gap-y-1 text-center">
                        {WEEKDAYS.map((day) => (
                            <div
                                key={day.label}
                                className={`py-1 text-[11px] font-bold tracking-wide ${day.className}`}
                            >
                                {day.label}
                            </div>
                        ))}
                    </div>

                    <div className="mt-1 grid min-h-0 flex-1 auto-rows-fr grid-cols-7 gap-y-0.5 overflow-hidden text-center">
                        {monthCells.map((cell) => {
                            const key = toDateKey(cell.date);
                            const selected = isSameDay(cell.date, selectedDate);
                            const isToday = isSameDay(cell.date, today);
                            const highlighted = selected || (isToday && cell.inMonth);
                            const hasDot = dotSet.has(key);
                            const hasDelay = delayDotSet.has(key);

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleSelectDate(cell.date)}
                                    className="relative flex min-h-0 min-w-0 items-center justify-center rounded py-0.5 hover:bg-[#f8fafc]"
                                >
                                    {hasDelay && (
                                        <span
                                            className="pointer-events-none absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[#b23609]"
                                            aria-hidden
                                        />
                                    )}
                                    <span
                                        className={[
                                            "inline-flex aspect-square h-full min-h-[22px] max-h-7 items-center justify-center rounded-full px-1 text-[12px] leading-none",
                                            highlighted
                                                ? "bg-[#3b82f6] font-semibold text-white"
                                                : cell.inMonth
                                                  ? "text-[#111827]"
                                                  : "text-[#c4c4c4]",
                                        ].join(" ")}
                                    >
                                        {cell.date.getDate()}
                                    </span>
                                    {hasDot && (
                                        <span
                                            className="pointer-events-none absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[#22c55e]"
                                            aria-hidden
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex min-h-[220px] min-w-0 flex-1 flex-col md:min-h-0">
                    <div className="mb-2 shrink-0 text-[13px] font-semibold text-[#111827]">
                        {selectedDate.getFullYear()}년 {String(selectedDate.getMonth() + 1).padStart(2, "0")}월 {String(selectedDate.getDate()).padStart(2, "0")}일
                    </div>
                    <ul className="flex min-h-0 flex-1 flex-col gap-[4px] overflow-y-auto pr-1">
                        {dayItems.map((item, idx) => {
                            const tag = HISTORY_TAG_STYLE[item.title] ?? DEFAULT_TAG_STYLE;
                            return (
                                <li
                                    key={`${toDateKey(selectedDate)}-${idx}`}
                                    className="flex items-center gap-[8px] rounded-[6px] border border-[#f1f5f9] bg-white px-[8px] py-[6px] text-[12px]"
                                >
                                    <span
                                        className="inline-flex shrink-0 items-center rounded-[4px] px-[6px] py-[2px] text-[11px] font-semibold"
                                        style={{color: tag.color, background: tag.bg}}
                                    >
                                        {item.title}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[#374151]">
                                        {item.name}
                                    </span>
                                </li>
                            );
                        })}
                        {dayItems.length === 0 && (
                            <li className="flex flex-1 items-center justify-center rounded-[6px] border border-dashed border-[#e5e7eb] bg-[#fafafa] text-[12px] text-[#9ca3af]">
                                선택한 날짜의 이력이 없습니다.
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export function TypeChartCard({title, subtitle, items}) {
    const safeItems = items ?? [];
    const total = sumCnt(safeItems);

    return (
        <div className="flex h-full min-h-0 w-full flex-col gap-[10px]">
            <div className="mb-2 flex shrink-0 items-baseline justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">
                    {title}
                    {subtitle && (
                        <span className="ml-2 inline-block text-[14px] font-medium leading-none text-[#666]">
                            : {subtitle}
                        </span>
                    )}
                </h3>
            </div>
            <div className="flex min-h-0 flex-1">
                <div className="mobile-hidden h-full w-[44%] min-w-0 shrink-0 overflow-hidden">
                    <ManagedAssetDoughnut items={safeItems} total={total} />
                </div>
                <div className="mobile-full h-full min-w-0 flex-1 overflow-hidden">
                    <ManagedAssetLineArea items={safeItems} />
                </div>
            </div>
        </div>
    );
}

export function LicenseSummaryCard({title, total, used, platform}) {
    const safeTotal = Number(total) || 0;
    const safeUsed = Number(used) || 0;
    const percent = safeTotal > 0 ? Math.min(100, (safeUsed / safeTotal) * 100) : 0;

    return (
        <div className="flex h-full min-h-0 w-full flex-col gap-[12px]">
            <div className="mb-6 flex shrink-0 items-center justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">{title}</h3>
            </div>

            <div className="flex shrink-0 flex-col gap-[8px]">
                <div className="flex items-center justify-between gap-2 text-[14px]">
                    <span className="font-medium text-[#333]">자산 현황</span>
                    <span className="text-[13px] text-[#666]">
                        <span className="font-semibold text-[#3b82f6]">
                            {safeUsed.toLocaleString()}
                        </span>
                        <span className="mx-1 text-[#9ca3af]">/</span>
                        {safeTotal.toLocaleString()}
                    </span>
                </div>
                <div
                    className="relative mb-4 h-[20px] w-full overflow-hidden rounded-full bg-[#e8e8e8]"
                    title={`${percent.toFixed(1)}%`}
                >
                    <div
                        className="h-full rounded-full bg-[#3b82f6] transition-[width] duration-300"
                        style={{width: `${percent}%`}}
                    />
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
                <div className="shrink-0 border-b border-[#e5e5e5] pb-[6px] text-[14px] font-medium text-[#333]">
                    서비스 플랫폼
                </div>
                <div className="flex min-h-0 flex-1 items-center justify-center">
                    <span className="text-[28px] font-bold tracking-wide text-[#3b82f6]">
                        {platform ?? "-"}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function AssetSummaryCard({title, items, period}) {
    return (
        <div className="flex h-full min-h-0 w-full flex-col gap-[4px]">
            <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">{title}</h3>
                {period && (
                    <span className="text-[12px] leading-none text-[#94a3b8]">{period}</span>
                )}
            </div>
            <div
                className="grid min-h-0 flex-1 gap-[4px] overflow-hidden"
                style={{gridAutoRows: "minmax(0, 1fr)"}}
            >
                {(items ?? []).map((item, index) => (
                    <SummaryRow
                        key={`${item.name}-${index}`}
                        label={item.name}
                        value={item.cnt}
                        icon={ITEM_ICON[item.name] ?? FileText}
                        iconBg={ITEM_ICON_BG[item.name]}
                    />
                ))}
                {(!items || items.length === 0) && (
                    <div className="flex flex-1 items-center justify-center rounded-[8px] border border-dashed border-[#e5e5e5] bg-white px-[10px] py-[16px] text-[12px] text-[#9ca3af]">
                        데이터가 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
}
